import React from "react";
import {Button} from "./ui/Button";
import {
    ArrowRight,
    ArrowUpDown,
    CircleCheck,
    HardDriveDownload,
    MoreHorizontal,
    MoreVertical,
    Pencil,
    Trash2
} from "lucide-react";
import {cn, formatDate, formatPrice} from "../lib/utils";
import {Link} from "react-router-dom";
import {DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger} from "./ui/DropDownMenu";
import {Badge} from "./ui/Badge";
import {auth} from "../firebase";
import {PiWifiHighFill, PiWifiLowFill} from "react-icons/pi";
import csvData from "../data/siteData.json"
import Piezometer from "./Piezometer";

export const columns = [
    {
        accessorKey: "title",
        header: ({ column }) => {
            return (
                <Button
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Title
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
    },
    {
        accessorKey: "price",
        header: ({ column }) => {
            return (
                <Button
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Price
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
        cell: ({ row }) => {
            const price = parseFloat(row.getValue("price") || "0");
            const formatted = new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "ZAR"
            }).format(price);

            return <div>{formatted}</div>
        }
    },
    {
        accessorKey: "isPublished",
        header: ({ column }) => {
            return (
                <Button
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Published
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
        cell: ({ row }) => {
            const isPublished = row.getValue("isPublished") || false;

            return (
                <Badge className={cn(
                    "bg-slate-500",
                    isPublished && "bg-sky-700"
                )}>
                    {isPublished ? "Published" : "Draft"}
                </Badge>
            )
        }
    },
    {
        id: "actions",
        cell: ({ row }) => {
            const { courseId } = row.original;

            return (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-4 w-8 p-0">
                            <span className="sr-only">Open menu</span>
                            <MoreHorizontal className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <Link to={`/teacher/courses/${courseId}`}>
                            <DropdownMenuItem>
                                <Pencil className="h-4 w-4 mr-2" />
                                Edit
                            </DropdownMenuItem>
                        </Link>
                    </DropdownMenuContent>
                </DropdownMenu>
            )
        }
    }
]

export const siteColumn = [
    {
        accessorKey: "siteId",
        header: ({ column }) => {
            return (
                <Button className="-ml-4"
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Site ID
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        }
    },
    {
        accessorKey: "siteName",
        header: ({ column }) => {
            return (
                <Button className="-ml-4"
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Site Name
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
        cell: ({ row }) => {
            const siteName = row.getValue("siteName")
            const siteId = row.getValue("siteId")

            return <Link to={`/dashboard/${auth?.currentUser?.uid}/analysis/${siteId}`} className="underline text-[#3898ec] font-medium">
                {siteName}
            </Link>
        }
    },
    {
        accessorKey: "siteAddress",
        header: ({ column }) => {
            return (
                <Button className="-ml-4"
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Site Address
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        }
    },
    {
        accessorKey: "status",
        header: ({column}) => {
            return (
                <Button className="-ml-4"
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Status
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
        cell: ({ row }) => {
            const status = row.getValue("status")

            return (
                <Badge className={cn("bg-red-500", status === "Active" && "bg-green-700"
                )}>
                    {status}
                </Badge>
            )
        }
    },
    {
        accessorKey: "siteImage",
        header: ({ column }) => {
            return (
                <Button className="-ml-4"
                        variant="ghost"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Site Image
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
        cell: ({ row }) => {
            const siteURL = row.getValue("siteImage");

            return (
                <img className="w-24 h-24" src={siteURL} alt="Site URL" />
            )
        }
    },
    {
        id: "download",
        header: ({ column }) => {
            return (
                <Button
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Download
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
        cell: ({ row }) => {
            const downloadCSV = () => {
                const blob = new Blob([csvData], { type: "text/csv" });
                const url = window.URL.createObjectURL(blob);

                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", "siteData.csv");
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                window.URL.revokeObjectURL(url);
            };

            return (
                <Button variant="secondary" className="flex items-center space-x-4" onClick={downloadCSV}>
                    <HardDriveDownload className="w-5 h-5" />
                    <p className="mt-4">Download .csv</p>
                </Button>
            )
        }
    },
    // {
    //     id: "actions",
    //     cell: ({ row }) => {
    //         const { courseId } = row.original;
    //
    //         return (
    //             <DropdownMenu>
    //                 <DropdownMenuTrigger asChild>
    //                     <Button variant="ghost" className="h-4 w-8 p-0">
    //                         <span className="sr-only">Open menu</span>
    //                         <MoreHorizontal className="h-4 w-4 text-black" />
    //                     </Button>
    //                 </DropdownMenuTrigger>
    //                 <DropdownMenuContent align="end">
    //                     <Link to={`/teacher/courses/${courseId}`}>
    //                         <DropdownMenuItem>
    //                             <Pencil className="h-4 w-4 mr-2 text-black" />
    //                             Edit
    //                         </DropdownMenuItem>
    //                     </Link>
    //                 </DropdownMenuContent>
    //             </DropdownMenu>
    //         )
    //     }
    // }
]

export const loggerColumns = [
    {
        accessorKey: "loggerId",
        header: ({ column }) => {
            return (
                <Button className="-ml-4"
                        variant="ghost"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    LoggerID
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
        cell: ({ row }) => {
            const loggerId = row.getValue("loggerId");
            const siteId = row.getValue("siteId");

            return (
                <Link to={`/dashboard/${auth?.currentUser?.uid}/analysis/${siteId}/${loggerId}`} className="underline text-[#3898ec] font-medium">
                    {loggerId}
                </Link>
            )
        }
    },
    {
        accessorKey: "loggerName",
        header: ({ column }) => {
            return (
                <Button className="-ml-4"
                        variant="ghost"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    LoggerName
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
        cell: ({ row }) => {
            const loggerName = row.getValue("loggerName");
            return <div>Line - {loggerName}</div>
        }
    },
    {
        accessorKey: "status",
        header: ({ column }) => {
            return (
                <Button className="-ml-4"
                        variant="ghost"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Status
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
        cell: ({ row }) => {
            const status = row.getValue("status");

            if (status === "active") {
                return (
                    <div className="w-8 h-8 rounded-full border-black border-2">
                        <div className="from-green-700 via-green-300 to-green-50 bg-gradient-to-br w-full h-full rounded-full">

                        </div>
                    </div>
                )
            } else {
                return <PiWifiLowFill className="mt-0.5" color="red" />
            }
        }
    },
    {
        accessorKey: "line",
        header: ({ column }) => {
            return (
                <Button className="-ml-4"
                        variant="ghost"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Line
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
        cell: ({ row }) => {
            const lineData = row.getValue("line");

            return (
                <div className="flex space-x-4">
                    {lineData.map((item, index) => (
                        <Piezometer item={item} key={index} />
                    ))}
                </div>
            )
        }
    },
    {
        accessorKey: "ch2",
        header: ({ column }) => {
            return (
                <Button className="-ml-4"
                        variant="ghost"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    {/*Ch 2*/}
                    {/*<ArrowUpDown className="ml-2 h-4 w-4" />*/}
                </Button>
            )
        },
        cell: ({ row }) => {
            const ch2 = row.getValue("ch2");

            if (ch2 === 'yes') {
                return <CircleCheck color="green" />
            }
        }
    },
    {
        accessorKey: "ch3",
        header: ({ column }) => {
            return (
                <Button className="-ml-4"
                        variant="ghost"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    {/*Ch 3*/}
                    {/*<ArrowUpDown className="ml-2 h-4 w-4" />*/}
                </Button>
            )
        },
        cell: ({ row }) => {
            const ch3 = row.getValue("ch3");

            if (ch3 === 'yes') {
                return <CircleCheck color="green" />
            }
        }
    },
    {
        accessorKey: "ch4",
        header: ({ column }) => {
            return (
                <Button className="-ml-4"
                        variant="ghost"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    {/*Ch 4*/}
                    {/*<ArrowUpDown className="ml-2 h-4 w-4" />*/}
                </Button>
            )
        },
        cell: ({ row }) => {
            const ch4 = row.getValue("ch4");

            if (ch4 === 'yes') {
                return <CircleCheck color="green" />
            }
        }
    },
    {
        accessorKey: "lastReading",
        header: ({ column }) => {
            return (
                <Button className="-ml-4"
                        variant="ghost"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Last Reading
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
    },
    {
        accessorKey: "loggerImageURL",
        header: ({ column }) => {
            return (
                <Button className="-ml-4"
                        variant="ghost"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Logger Image
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
        cell: ({ row }) => {
            const url = row.getValue("loggerImageURL");

            return (
                <>{url && <img src={url} className="h-24 w-24" alt="logger image url"/>}</>
            )
        }
    },
    {
        id: "actions",
        header: ({ column }) => {
            return (
                <Button className="-ml-4"
                        variant="ghost"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Download
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
        cell: ({ row }) => {
            const downloadCSV = () => {
                const blob = new Blob([csvData], { type: "text/csv" });
                const url = window.URL.createObjectURL(blob);

                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", "loggers.csv");
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                window.URL.revokeObjectURL(url);
            };

            return (
                <Button variant="secondary" className="flex items-center space-x-4" onClick={downloadCSV}>
                    <HardDriveDownload className="w-5 h-5" />
                    <p className="mt-4">Download .csv</p>
                </Button>
            )
        }
    }
]

export const piezometerColumns = [
    {
        accessorKey: "status",
        header: ({ column }) => {
            return (
                <Button className="-ml-4"
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Status
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
        // cell: ({ row }) => {
        //     const url = row.getValue("status");
        //
        //     return (
        //         <img src={url} alt=""/>
        //     )
        // }
    },
    {
        accessorKey: "loggerId",
        header: ({ column }) => {
            return (
                <Button className="-ml-4"
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Channel ID
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
    },
    {
        accessorKey: "totalLength",
        header: ({ column }) => {
            return (
                <Button className="-ml-4"
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Total Length
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
    },
    {
        accessorKey: "depthToWater",
        header: ({ column }) => {
            return (
                <Button className="-ml-4"
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Depth To Water
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
    },
    {
        accessorKey: "standpipeHeight",
        header: ({ column }) => {
            return (
                <Button className="-ml-4"
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Standpipe Height
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
    },
    {
        accessorKey: "date",
        header: ({ column }) => {
            return (
                <Button className="-ml-4"
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Last Reading
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
    },
    // {
    //     accessorKey: "engineeringValue",
    //     header: ({ column }) => {
    //         return (
    //             <Button className="-ml-4"
    //                 variant="ghost"
    //                 onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
    //             >
    //                 Engineering Value (kPa)
    //                 <ArrowUpDown className="ml-2 h-4 w-4" />
    //             </Button>
    //         )
    //     },
    // },
    // {
    //     accessorKey: "engineeringUnits",
    //     header: ({ column }) => {
    //         return (
    //             <Button className="-ml-4"
    //                 variant="ghost"
    //                 onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
    //             >
    //                 Engineering Units
    //                 <ArrowUpDown className="ml-2 h-4 w-4" />
    //             </Button>
    //         )
    //     },
    // },
    {
        accessorKey: "comments",
        header: ({ column }) => {
            return (
                <Button className="-ml-4"
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Comments
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
    },
    // {
    //     accessorKey: "cptuReport",
    //     header: ({ column }) => {
    //         return (
    //             <Button className="-ml-4"
    //                 variant="ghost"
    //                 onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
    //             >
    //                 CPTu Report
    //                 <ArrowUpDown className="ml-2 h-4 w-4" />
    //             </Button>
    //         )
    //     },
    // },
    // {
    //     accessorKey: "readingAtLastCPTu",
    //     header: ({ column }) => {
    //         return (
    //             <Button className="-ml-4"
    //                     variant="ghost"
    //                     onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
    //             >
    //                 Reading at Last CPTu
    //                 <ArrowUpDown className="ml-2 h-4 w-4" />
    //             </Button>
    //         )
    //     },
    // },
    {
        accessorKey: "criticalDepth",
        header: ({ column }) => {
            return (
                <Button className="-ml-4"
                        variant="ghost"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Critical Depth
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
    },
    {
        accessorKey: "alertDepth",
        header: ({ column }) => {
            return (
                <Button className="-ml-4"
                        variant="ghost"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Alert Depth
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
    },
    {
        id: "actions",
        cell: ({ row }) => {
            const { courseId } = row.original;

            return (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-4 w-8 p-0">
                            <span className="sr-only">Open menu</span>
                            <MoreHorizontal className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <Link to={`/teacher/courses/${courseId}`}>
                            <DropdownMenuItem>
                                <Pencil className="h-4 w-4 mr-2" />
                                Edit
                            </DropdownMenuItem>
                        </Link>
                    </DropdownMenuContent>
                </DropdownMenu>
            )
        }
    }
]

export const customersColumns = [
    {
        accessorKey: "firstName",
        header: ({ column }) => {
            return (
                <div
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    First Name
                    {/*<ArrowUpDown className="ml-2 h-4 w-4" />*/}
                </div>
            )
        },

    },
    {
        accessorKey: "surname",
        header: ({ column }) => {
            return (
                <div
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Surname
                    {/*<ArrowUpDown className="ml-2 h-4 w-4" />*/}
                </div>
            )
        },

    },
    {
        accessorKey: "email",
        header: ({ column }) => {
            return (
                <div
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Email Address
                    {/*<ArrowUpDown className="ml-2 h-4 w-4" />*/}
                </div>
            )
        },
    },
    {
        accessorKey: "totalPoints",
        header: ({ column }) => {
            return (
                <div
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Points
                    {/*<ArrowUpDown className="ml-2 h-4 w-4" />*/}
                </div>
            )
        },
    },
    {
        accessorKey: "dateJoined",
        header: ({ column }) => {
            return (
                <div
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Date Joined
                    {/*<ArrowUpDown className="ml-2 h-4 w-4" />*/}
                </div>
            )
        },
        cell: ({ row }) => {
            const dateJoined = row.getValue("dateJoined");

            return (
                <p>
                    {formatDate(dateJoined)}
                </p>
            )
        }
    },

]

export const approvalsColumns = [
    {
        accessorKey: "email",
        header: ({ column }) => {
            return (
                <Button
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Email Address
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
    },
    {
        accessorKey: "orderID",
        header: ({ column }) => {
            return (
                <Button
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Order ID
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
    },
    {
        accessorKey: "purchaseAmount",
        header: ({ column }) => {
            return (
                <Button
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Purchase Amount
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },

    },
    {
        accessorKey: "purchaseDate",
        header: ({ column }) => {
            return (
                <Button
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Purchase Date
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },

    }
]

export const successfulPurchasesColumns = [
    {
        accessorKey: "orderID",
        header: ({ column }) => {
            return (
                <Button
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Order ID
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
    },
    {
        accessorKey: "orderNumber",
        header: ({ column }) => {
            return (
                <Button
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Order Number
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
    },
    {
        accessorKey: "email",
        header: ({ column }) => {
            return (
                <Button
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Email Address
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },

    },
    {
        accessorKey: "purchaseAmount",
        header: ({ column }) => {
            return (
                <Button
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Purchase Amount
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },

    },
    {
        accessorKey: "commission",
        header: ({ column }) => {
            return (
                <Button
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Commission
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },

    },
    {
        accessorKey: "purchaseDate",
        header: ({ column }) => {
            return (
                <Button
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Purchase Date
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },

    }
]

export const failedPurchasesColumns = [
    {
        accessorKey: "orderID",
        header: ({ column }) => {
            return (
                <Button
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Order ID
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
    },
    {
        accessorKey: "purchaseAmount",
        header: ({ column }) => {
            return (
                <Button
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Purchase Amount
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },

    },
    {
        accessorKey: "purchaseDate",
        header: ({ column }) => {
            return (
                <Button
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Purchase Date
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },

    }
]

export const businessColumns = [
    {
        accessorKey: "businessName",
        header: ({ column }) => {
            return (
                <Button
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Business Name
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
    },
    {
        accessorKey: "category",
        header: ({ column }) => {
            return (
                <Button
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Category
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        }
    },
    {
        id: "actions",
        cell: ({ row }) => {
            const { businessId } = row.original;

            return (
                <Link to={`/account/analytics/${businessId}`} className="w-full">
                    <Button size="sm" variant="ghost" className="flex gap-x-1 ml-auto">
                        Proceed
                        <ArrowRight />
                    </Button>
                </Link>
            )
        }
    }
]

export const rewardsColumns = [
    {
        accessorKey: "rewardName",
        header: ({ column }) => {
            return (
                <Button className="-ml-4"
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Reward Name
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
    },
    {
        accessorKey: "maxPoints",
        header: ({ column }) => {
            return (
                <Button className="-ml-4"
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Maximum Points
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        }
    },
    {
        id: "actions",
        header: ({ column }) => {
            return (
                <Button className="-ml-4"
                        variant="ghost"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Date Created
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
        cell: ({ row }) => {
            const { dateCreated } = row.original;

            return (
                <p>
                    {formatDate(dateCreated)}
                </p>
            )
        }
    },
    {
        id: "actions",
        cell: ({ row }) => {
            return (
                <DropdownMenu className="my-0 py-0">
                    <DropdownMenuTrigger className="my-0">
                        <Button size="icon" variant="ghost">
                            <MoreVertical className="w-5 h-5"/>
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                        <DropdownMenuItem className="flex gap-x-2 cursor-pointer">
                            <Pencil className="h-4 w-4 ml-0.5" />
                            Edit
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            )
        }
    }
]

export const historyColumns = [
    {
        accessorKey: "points",
        header: ({ column }) => {
            return (
                <Button className="-ml-4"
                        variant="ghost"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Total Amount
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
        cell: ({ row }) => {
            const { points } = row.original;

            return (
                <p>
                    {formatPrice(points)}
                </p>
            )
        }
    },
    {
        accessorKey: "points",
        header: ({ column }) => {
            return (
                <Button className="-ml-4"
                        variant="ghost"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Points Gained
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        }
    },
    {
        id: "dateJoined",
        header: ({ column }) => {
            return (
                <Button className="-ml-4"
                        variant="ghost"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Date Created
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
        cell: ({ row }) => {
            const { dateCreated } = row.original;

            return (
                <p>
                    {formatDate(dateCreated)}
                </p>
            )
        }
    },
    // {
    //     id: "actions",
    //     cell: ({ row }) => {
    //         return (
    //             <DropdownMenu className="my-0 py-0">
    //                 <DropdownMenuTrigger className="my-0">
    //                     <Button size="icon" variant="ghost">
    //                         <MoreVertical className="w-5 h-5"/>
    //                     </Button>
    //                 </DropdownMenuTrigger>
    //                 <DropdownMenuContent>
    //                     <DropdownMenuItem className="flex gap-x-2 cursor-pointer">
    //                         <Pencil className="h-4 w-4 ml-0.5" />
    //                         Edit
    //                     </DropdownMenuItem>
    //                 </DropdownMenuContent>
    //             </DropdownMenu>
    //         )
    //     }
    // }
]

export const userRewardsColumns = [
    {
        accessorKey: "rewardName",
        header: ({ column }) => {
            return (
                <Button className="-ml-4"
                        variant="ghost"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Reward Name
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
    },
    {
        accessorKey: "maxPoints",
        header: ({ column }) => {
            return (
                <Button className="-ml-4"
                        variant="ghost"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Maximum Points
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        }
    },
    {
        id: "dateJoined",
        header: ({ column }) => {
            return (
                <Button className="-ml-4"
                        variant="ghost"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Date Created
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
        cell: ({ row }) => {
            const { dateCreated } = row.original;

            return (
                <p>
                    {formatDate(dateCreated)}
                </p>
            )
        }
    },
    {
        id: "isRewarded",
        header: ({ column }) => {
            return (
                <Button className="-ml-4"
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Status
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
        cell: ({ row }) => {
            const isRewarded = row.getValue("isRewarded") || false;

            return (
                <Badge className={cn(
                    "bg-slate-500",
                    isRewarded && "bg-sky-700"
                )}>
                    {isRewarded ? "Rewarded" : "Pending"}
                </Badge>
            )
        }
    }
]

export const redeemColumns = [
    {
        accessorKey: "fullName",
        header: ({ column }) => {
            return (
                <Button className="-ml-4"
                        variant="ghost"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Full Name
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
    },
    {
        accessorKey: "rewardName",
        header: ({ column }) => {
            return (
                <Button className="-ml-4"
                        variant="ghost"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Reward Name
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        }
    },
    {
        accessorKey: "pointsRedeemed",
        header: ({ column }) => {
            return (
                <Button className="-ml-4"
                        variant="ghost"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Points Redeemed
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        }
    },
    {
        id: "dateCreated",
        header: ({ column }) => {
            return (
                <Button className="-ml-4"
                        variant="ghost"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Date Created
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
        cell: ({ row }) => {
            const { dateCreated } = row.original;

            return (
                <p>
                    {formatDate(dateCreated)}
                </p>
            )
        }
    },
    {
        id: "isRewarded",
        header: ({ column }) => {
            return (
                <Button className="-ml-4"
                        variant="ghost"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Status
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
        cell: ({ row }) => {
            const isRewarded = row.getValue("isRewarded") || false;

            return (
                <Badge className={cn(
                    "bg-slate-500",
                    isRewarded && "bg-sky-700"
                )}>
                    {isRewarded ? "Rewarded" : "Pending"}
                </Badge>
            )
        }
    }
]
